import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Icon,
  Pane,
  SegmentedControl,
  SegmentedControlOptionType,
  Text,
} from '@fullstory/components';
import { serializeSelectorPart } from '@fullstory/selectors';
import { FormattedMessage } from 'react-intl';
import { classNames } from '@fullstory/util/fmt';
import {
  getNextActiveState,
  SelectorPiece,
  selectorPiecesToSelectorPart,
} from '../../utils/selector-tree';
import css from './index.module.scss';
import type { SelectorTreeHookState } from '../../hooks/useSelectorTree';
import { intl } from '@fullstory/intl';
import { SelectorTrimmingBanner, SelectorTrimmingState } from '../SelectorTrimmingBanner';

function removeBrackets(str: string): string {
  if (str.startsWith('[') && str.endsWith(']')) {
    return str.slice(1, -1);
  }
  return str;
}

const SelectorPiecePill = ({
  piece,
  onClick,
}: {
  piece: SelectorPiece;
  onClick: (nodeId: string) => void;
}) => {
  const [hovered, setHovered] = useState(false);
  const [maxWidth, setMaxWidth] = useState<number | 'none'>(320);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    if (hovered) {
      timeoutId = setTimeout(() => setMaxWidth(5000), 800);
    } else {
      setMaxWidth(400);
    }
    return () => clearTimeout(timeoutId);
  }, [hovered]);

  const activeColor = piece.type == 'tag' ? 'blue-70' : 'blue-50';
  const color = piece.activeState == 'active' ? activeColor : undefined;

  const selectorPart = selectorPiecesToSelectorPart([piece]);
  const display = removeBrackets(serializeSelectorPart(selectorPart));

  return (
    <Pane
      // @ts-ignore
      backgroundColor={color}
      data-component="SelectorPiecePill"
      padding={4}
      borderRadius={4}
      border={piece.activeState == 'active' ? undefined : 'standard'}
      onClick={() => onClick(piece.nodeId)}
      className={classNames(css.pill, {
        [css.active]: piece.activeState == 'active',
      })}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      maxWidth={maxWidth}
    >
      <Text
        color={piece.activeState == 'active' ? 'default-inverse' : 'secondary'}
        appearance="bodySmall"
        noWrap
      >
        {display}
      </Text>
    </Pane>
  );
};

const segmentedControlOptions: SegmentedControlOptionType[] = [
  {
    label: intl.formatMessage({ defaultMessage: 'Trimmed', id: '7wLBUP' }),
    id: 'trimmed',
  },
  {
    label: intl.formatMessage({ defaultMessage: 'Full', id: 'NYnkyd' }),
    id: 'full',
  },
];

export const SelectorTree = ({
  selectorParts,
  setSelectorParts,
  trimming,
  trimmedSelectorParts,
  onHoverSelectorPart,
  abortTrim,
  trimState,
}: {
  selectorParts: SelectorTreeHookState['selectorParts'];
  setSelectorParts: SelectorTreeHookState['setSelectorParts'];
  trimming: SelectorTreeHookState['trimming'];
  trimmedSelectorParts: SelectorTreeHookState['trimmedSelectorParts'];
  onHoverSelectorPart: SelectorTreeHookState['onHoverSelectorPart'];
  abortTrim: () => void;
  trimState: SelectorTrimmingState;
}) => {
  const [mode, setMode] = useState(segmentedControlOptions[0]!);

  const togglePiece = useCallback(
    (nodeId: string) => {
      setSelectorParts(sp =>
        sp
          ? sp.map(part =>
              part.map(p =>
                p.nodeId === nodeId ? { ...p, activeState: getNextActiveState(p.activeState) } : p,
              ),
            )
          : sp,
      );
    },
    [setSelectorParts],
  );

  const hoverPart = useCallback(
    (event: React.MouseEvent) => {
      if (trimming) return;

      const partIndex = event.currentTarget.getAttribute('data-part-index');
      if (!partIndex) return;

      onHoverSelectorPart(Number(partIndex));
    },
    [onHoverSelectorPart, trimming],
  );

  const unhoverAllParts = useCallback(() => {
    onHoverSelectorPart(undefined);
  }, [onHoverSelectorPart]);

  if (selectorParts?.length == 0) {
    return (
      <Box data-component="NoSelector" padding={8} testId="invalid-selector">
        <Text appearance="bodyMedium">
          <FormattedMessage
            defaultMessage="Click an element in the preview, or enter a selector."
            id="W5Oyw/"
          />
        </Text>
      </Box>
    );
  }

  if (!selectorParts)
    return (
      <Pane
        data-component="InvalidSelector"
        borderRadius={4}
        padding={8}
        testId="invalid-selector"
        className={css.banner}
      >
        <Flex justifyContent="space-between" gap={12}>
          <Flex gap={12}>
            <Box>
              <Icon name="warning" color="default" size={16} />
            </Box>
            <Text appearance="bodyMedium">
              <FormattedMessage defaultMessage="Invalid selector" id="CIp7Ug" />
            </Text>
          </Flex>
        </Flex>
      </Pane>
    );
  if (selectorParts.length == 0) return null;

  // when we're running the optimizer, we're going to show the "loading" parts
  const partsToShow = trimming ? trimmedSelectorParts : selectorParts;
  const showHidden = mode.id === 'full';
  // we don't need to show the toggle if all parts are visible
  const showToggle = selectorParts.some(part => part.some(p => p.activeState === 'hidden'));

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: '4px', userSelect: 'none' }}
      data-component="SelectorTree"
    >
      {showToggle && (
        <Flex justifyContent="flex-end">
          <Box width="50%" paddingBottom={4}>
            <SegmentedControl
              onChange={mode => {
                setMode(mode);
                // all inactive pieces should be made hidden now
                setSelectorParts(sp =>
                  sp
                    ? sp.map(part =>
                        part.map(p =>
                          p.activeState === 'inactive' ? { ...p, activeState: 'hidden' } : p,
                        ),
                      )
                    : sp,
                );
              }}
              selected={mode}
              options={segmentedControlOptions}
              size="x-small"
            />
          </Box>
        </Flex>
      )}
      <div style={{ position: 'relative', height: '100%' }}>
        <Box flexDirection="column" display="flex" overflow="auto" gap={4} maxHeight={464}>
          {partsToShow
            // only show parts that have at least one visible piece
            .filter(
              sp =>
                showHidden ||
                sp.some(sp => sp.activeState == 'active' || sp.activeState == 'inactive'),
            )
            .map((selectorPart, index) => (
              <div
                key={selectorPart.reduce((acc, curr) => acc + curr.nodeId, '')}
                style={{
                  paddingLeft: `${index * 12}px`,
                }}
              >
                <div
                  className={classNames(css.selectorPart)}
                  data-part-index={index}
                  style={{
                    borderRadius: '4px',
                    display: 'inline-flex',
                    flexWrap: 'wrap',
                    gap: '4px',
                  }}
                  onMouseEnter={hoverPart}
                  onMouseLeave={unhoverAllParts}
                >
                  {selectorPart
                    .filter(
                      p => showHidden || p.activeState == 'active' || p.activeState == 'inactive',
                    )
                    .map(p => (
                      <SelectorPiecePill key={p.nodeId} piece={p} onClick={togglePiece} />
                    ))}
                </div>
              </div>
            ))}
        </Box>
        {trimming && <SelectorTrimmingBanner state={trimState} abortTrim={abortTrim} />}
      </div>
    </div>
  );
};
